import React from 'react';
import { Coins, ListChecks } from 'lucide-react';
import { BomPricing } from '../../../hooks/useBomPricing';
import { formatCredits } from '../../../utils/credits';

/**
 * The credits line on Wizard Step 1. Extracting a BOM is free — the BOM is
 * charged per component when the buyer starts supplier discovery on step 2 —
 * so before Continue the line states that rule, and after it says what was
 * extracted and what it will cost to search for.
 *
 * Credits are an account fact, not an AI surface: neutral chrome, and no
 * violet anywhere in here. Nothing is ever invented — when the rate or the
 * balance cannot be read, the words that would have needed it are left out.
 */

/** What this session's extraction produced. */
export interface ExtractionSummary {
  components: number;
  /** Lines the completeness check flagged (`completeness.incomplete.length`). */
  needsAttention: number;
}

export interface UploadCreditsNoteProps {
  pricing: BomPricing;
  /** Present once Continue has extracted the BOM this session. */
  extraction?: ExtractionSummary | null;
}

const plural = (count: number, word: string) => `${formatCredits(count)} ${word}${count === 1 ? '' : 's'}`;

/** The rule, before anything is extracted: what discovery costs, and what the org has. */
const PriceRule: React.FC<{ pricing: BomPricing }> = ({ pricing }) => {
  if (pricing.rate === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-s4 gap-y-[2px] text-body-sm text-ink-secondary">
      <Coins className="h-[13px] w-[13px] shrink-0 text-ink-muted" />
      <span>
        {pricing.rate === 0
          ? 'Supplier discovery on this BOM is free.'
          : `${formatCredits(pricing.rate)} credits per component when you start supplier discovery.`}
      </span>
      {pricing.balance !== null && (
        <span className="text-ink-muted">Your balance is {formatCredits(pricing.balance)}.</span>
      )}
    </div>
  );
};

/** What came out of the files, and what searching for it will cost. */
const Extracted: React.FC<{ extraction: ExtractionSummary; rate: number | null }> = ({ extraction, rate }) => (
  <div className="flex flex-wrap items-center gap-x-s4 gap-y-[2px] text-body-sm text-ink-body">
    <ListChecks className="h-[13px] w-[13px] shrink-0 text-ink-muted" />
    <span className="font-bold text-ink">{plural(extraction.components, 'component')} extracted</span>
    {rate !== null && (
      <>
        <span className="text-ink-muted">·</span>
        <span>
          {rate === 0
            ? 'discovery is free'
            : `${formatCredits(rate)} credits each when you start discovery`}
        </span>
      </>
    )}
    {extraction.needsAttention > 0 && (
      <>
        <span className="text-ink-muted">·</span>
        <span className="text-warn-ink">
          {plural(extraction.needsAttention, 'line')} need{extraction.needsAttention === 1 ? 's' : ''} attention
          — open the BOM on the next step
        </span>
      </>
    )}
  </div>
);

export const UploadCreditsNote: React.FC<UploadCreditsNoteProps> = ({ pricing, extraction }) =>
  extraction ? <Extracted extraction={extraction} rate={pricing.rate} /> : <PriceRule pricing={pricing} />;

export default UploadCreditsNote;
